import { cn } from '@/lib/utils';
import {
  type ScoreBreakdown,
  formatScore,
} from '@/lib/selectionScore';

/**
 * Small badge that displays the computed selection score with a tooltip
 * breaking down base power, streak bonuses/penalties, and flag multipliers.
 */
export function ScoreBadge({
  breakdown,
  className,
}: {
  breakdown: ScoreBreakdown;
  className?: string;
}) {
  const lines: string[] = [];
  lines.push(`Score ${formatScore(breakdown.score)}`);
  lines.push(`Base (M1 power): ${formatScore(breakdown.base)}`);
  const streakParts: string[] = [];
  if (breakdown.notPickedBonus > 0) {
    streakParts.push(`+${(breakdown.notPickedBonus * 100).toFixed(0)}% not picked`);
  }
  if (breakdown.notRegisteredBonus > 0) {
    streakParts.push(`+${(breakdown.notRegisteredBonus * 100).toFixed(0)}% not registered`);
  }
  if (breakdown.subsOnlyBonus > 0) {
    streakParts.push(`+${(breakdown.subsOnlyBonus * 100).toFixed(0)}% subs-only`);
  }
  if (breakdown.noShowPenalty > 0) {
    streakParts.push(`−${(breakdown.noShowPenalty * 100).toFixed(0)}% no-show`);
  }
  if (breakdown.latePenalty > 0) {
    streakParts.push(`−${(breakdown.latePenalty * 100).toFixed(0)}% late`);
  }
  if (streakParts.length > 0) {
    lines.push(`Streak factor ×${breakdown.streakFactor.toFixed(2)} (${streakParts.join(', ')})`);
  }
  if (breakdown.yellow) lines.push('Yellow card ×0.9');
  if (breakdown.green) lines.push('Green flag ×2.5');
  if (breakdown.slotBonus) lines.push('Slot preference ×1.1');
  if (breakdown.forced) {
    const label =
      breakdown.forced === 'r5'
        ? 'R5 — forced pick'
        : breakdown.forced === 'r4'
          ? 'R4 — forced pick'
          : 'Never-Sub — forced pick';
    lines.push(label);
  } else if (breakdown.softForce) {
    lines.push('Green flag — soft force');
  }
  if (breakdown.redCarded) lines.push('Red-carded — excluded from autofill');

  const tone = breakdown.redCarded
    ? 'bg-red-900/40 text-red-300 border-red-800'
    : breakdown.forced
      ? 'bg-amber-900/40 text-amber-200 border-amber-800/60'
      : breakdown.softForce
        ? 'bg-emerald-900/40 text-emerald-200 border-emerald-800/60'
        : 'bg-slate-800/70 text-slate-300 border-slate-700';

  // Show the streak multiplier as a subtle inline suffix when it materially
  // differs from 1.0 — makes the "why is this score so high/low" visible
  // without having to hover for the tooltip.
  const showStreak = Math.abs(breakdown.streakFactor - 1) >= 0.01;
  const streakTone =
    breakdown.streakFactor > 1
      ? 'text-orange-300'
      : 'text-red-300';

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span
        title={lines.join('\n')}
        className={cn(
          'inline-flex items-center rounded border px-1 py-0.5 text-[10px] font-semibold tabular-nums',
          tone,
        )}
      >
        {formatScore(breakdown.score)}
      </span>
      {showStreak && (
        <span
          title={lines.join('\n')}
          className={cn('text-[10px] font-semibold tabular-nums', streakTone)}
        >
          ×{breakdown.streakFactor.toFixed(2)}
        </span>
      )}
    </span>
  );
}
